import { useEffect, useState } from 'react';
import { SignUpWrapper } from './SignUpPage.styles';
import useExternalMsal, { EXTERNAL_SIGNUP_PENDING, EXTERNAL_SIGNUP_STORAGE_KEY } from '../../auth/useExternalMsal';

// Landing page for OrcaWebsite's external (customer) sign-in/sign-up flow,
// backed by Microsoft Entra External ID (CIAM). Handles:
//  - the initial entry point (redirects to the hosted sign-in/sign-up experience)
//  - the return redirect once a new user has submitted their details, showing a
//    "pending approval" confirmation message (mirrors ArmadaPortal's LoginPage)
//  - the return redirect for an already-approved external user signing in
type ViewState = 'processing' | 'pending_approval' | 'signed_in' | 'idle';

const hasExternalAuthResponseInUrl = (): boolean =>
    window.location.hash.length > 0 ||
    window.location.search.includes('code=') ||
    window.location.search.includes('id_token=') ||
    window.location.search.includes('state=');

const readInitialViewState = (): ViewState => {
    if (hasExternalAuthResponseInUrl()) {
        return 'processing';
    }

    const storedStatus = sessionStorage.getItem(EXTERNAL_SIGNUP_STORAGE_KEY);
    if (storedStatus === EXTERNAL_SIGNUP_PENDING) {
        return 'pending_approval';
    }
    return 'processing';
};

const SignUpPage = () => {
    const { loginExternal, handleExternalRedirect } = useExternalMsal();
    const [viewState, setViewState] = useState<ViewState>(readInitialViewState);

    useEffect(() => {
        if (viewState !== 'processing') {
            return;
        }

        handleExternalRedirect().then((state) => {
            if (state === 'pending_approval') {
                setViewState('pending_approval');
            } else if (state === 'signed_in') {
                setViewState('signed_in');
            } else {
                setViewState('idle');
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (viewState === 'processing') {
        return null;
    }

    return (
        <SignUpWrapper>
            {viewState === 'pending_approval' && (
                <>
                    <h1>Thank you for signing up!</h1>
                    <p>
                        Your account and access request must be approved by the Odfjell team before you can access OrcaWebsite. You will be
                        notified by email once your request has been approved.
                    </p>
                </>
            )}
            {viewState === 'signed_in' && (
                <>
                    <h1>You&apos;re signed in</h1>
                    <p>Thanks for signing in to OrcaWeb.</p>
                </>
            )}
            {viewState === 'idle' && (
                <>
                    <h1>OrcaWeb</h1>
                    <p>Please click on the button below to sign in or sign up for access to OrcaWeb.</p>
                    <button onClick={loginExternal}>Continue</button>
                </>
            )}
            <div className="contact-details">
                For questions, please contact
                <a href="mailto:fleetperformance@odfjell.com"> fleetperformance@odfjell.com</a>
            </div>
        </SignUpWrapper>
    );
};

export default SignUpPage;
